import { BLACK_TEXT, WHITE } from 'constants/styling/theme'
import { EditionType, getEditionTypeString } from '../ClientPhotoStudio.context'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AnalyticsEvent } from 'utils/analytics'
import { DEFAULT_BRUSH_SIZE } from './MaskDrawer.controller'
import { FileType } from 'constants/misc'
import { MaskDrawingType } from 'constants/visualStaging/visualStagingTypes'
import { PhotoStudioFileType } from 'constants/visual/photoStudio'
import { LabEraserObjects } from '../lab/useLabEraserObjects.hook'
import { binarizeLabMaskPixels } from '../lab/labMask'
import { labObjectIdAt, labObjectRect } from '../lab/labObjects'
import constate from 'constate'
import { logger } from 'utils/logging'

/** How far a press on an object may travel and still be a click on it rather than a stroke (the Lab Playground's) */
const OBJECT_TAP_TOLERANCE_PX = 6

// Global mask storage to persist across component unmounting/remounting
const globalMaskStorage = new Map<string, {
  maskData: string | null,
  lastImageUrl: string
}>()

export const [MaskDrawerProvider, useMaskDrawer] = constate(({
  imageUrl,
  currentPreviewImage,
  onMaskChange,
  onPreviewImageChange,
  clearMaskTrigger,
  isVisualStaging,
  logPhotoStudioEvent,
  assignmentId,
  sourceVisualFilename,
  isProcessingImage,
  isSaving,
  labObjects,
}: {
  /** The URL of the image to be edited */
  imageUrl: string
  /** The URL of the current preview image */
  currentPreviewImage: string
  /** Callback function to be called when the mask changes */
  onMaskChange?: () => void
  /** Callback function to be called when the preview image changes */
  onPreviewImageChange?: (imageUrl: string) => void
  /** The trigger to clear the mask */
  clearMaskTrigger?: number
  /** Whether the mask drawer is a magic eraser tool */
  isMagicEraserTool?: boolean
  /** Whether the mask drawer is a visual staging tool */
  isVisualStaging?: boolean
  /** Analytics logging function */
  logPhotoStudioEvent?: (eventName: string, params?: {}) => void
  /** Assignment ID for analytics */
  assignmentId?: string
  /** Source visual filename for analytics */
  sourceVisualFilename?: string
  /** Whether image is being processed */
  isProcessingImage?: boolean
  /** Whether result is being saved */
  isSaving?: boolean
  /** Lab SDK engine, eraser: the objects detected in the photo, selectable with a click */
  labObjects?: LabEraserObjects | null
}) => {

  const imageRef = useRef<HTMLImageElement>(null)
  const previewMaskCanvasRef = useRef<HTMLCanvasElement>(null)
  const storedMaskCanvasRef = useRef<HTMLCanvasElement>(null)
  const previewImageSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })
  // Store the last position to draw continuous lines
  const lastPosRef = useRef<{ x: number; y: number } | null>(null)
  // Store canvas contexts to avoid recreating them on every draw
  const previewMaskCtxRef = useRef<CanvasRenderingContext2D | null>(null)
  const storedMaskCtxRef = useRef<CanvasRenderingContext2D | null>(null)
  // Store current stroke data
  const currentStrokeRef = useRef<{
    path: Array<{ x: number; y: number }>
    brushSize: number
    maskType: MaskDrawingType
    isDrawing: boolean
  }>({
    path: [],
    brushSize: 0,
    maskType: MaskDrawingType.MAGIC_ERASER,
    isDrawing: false
  })

  // Lab SDK engine, eraser: the detected objects (useLabEraserObjects), selectable with a click. One at a time — the
  // Lab eraser regenerates one area around everything marked — so clicking another object replaces the selection. The
  // selection has its own natural-size layer, composed with the strokes for the preview and the export.
  const objectMap = labObjects?.status === 'ready' ? labObjects.map : undefined
  const objectMapRef = useRef(objectMap)
  objectMapRef.current = objectMap
  const objectsCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const selectedObjectIdRef = useRef(0)
  const [selectedObjectId, setSelectedObjectId] = useState(0)
  const hoveredObjectIdRef = useRef(0)
  const [hoveredObject, setHoveredObject] = useState<{ id: number, x: number, y: number } | null>(null)
  const pendingTapRef = useRef<{ x: number, y: number, clientX: number, clientY: number, objectId: number } | null>(null)

  const [previewImageMagicEraser, setPreviewImageMagicEraser] = useState<string>(currentPreviewImage)
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE)

  const [isDrawing, setIsDrawing] = useState(false)
  const [maskUrl, setMaskUrl] = useState<string | null>(null)
  const [canvasReady, setCanvasReady] = useState(false)
  const [isApplyingChanges, setIsApplyingChanges] = useState(false)
  // Track number of masks applied for analytics (independent of deprecated unifiedHistory)
  const [numberOfMasksApplied, setNumberOfMasksApplied] = useState(0)

  // Mark visual staging feature change
  const markVisualStagingFeatureChange = useCallback(() => {
    setIsApplyingChanges(true)

    // Reset flags after a short delay to allow mask restoration
    setTimeout(() => {
      setIsApplyingChanges(false)
    }, 500)
  }, [])

  // Listen for visual staging feature changes
  useEffect(() => {
    const handleVisualStagingFeatureChange = () => {
      markVisualStagingFeatureChange()
    }
    window.addEventListener('visualStagingFeatureChange', handleVisualStagingFeatureChange)
    return () => {
      window.removeEventListener('visualStagingFeatureChange', handleVisualStagingFeatureChange)
    }
  }, [markVisualStagingFeatureChange])

  // Create a unique key for this mask session
  const maskSessionKey = `${imageUrl}-${isVisualStaging ? 'visual-staging' : 'magic-eraser'}`

  // Save mask data to global storage
  const saveMaskToGlobalStorage = useCallback(() => {
    if (storedMaskCanvasRef.current) {
      const maskData = storedMaskCanvasRef.current.toDataURL('image/png')
      globalMaskStorage.set(maskSessionKey, {
        maskData,
        lastImageUrl: imageUrl
      })
    }
  }, [maskSessionKey, imageUrl])

  // Restore mask data from global storage
  const restoreMaskFromGlobalStorage = useCallback(() => {
    const stored = globalMaskStorage.get(maskSessionKey)
    if (stored && stored.lastImageUrl === imageUrl && stored.maskData && storedMaskCanvasRef.current) {

      const img = new Image()
      img.onload = () => {
        if (storedMaskCtxRef.current && storedMaskCanvasRef.current) {
          storedMaskCtxRef.current.clearRect(0, 0, storedMaskCanvasRef.current.width, storedMaskCanvasRef.current.height)
          storedMaskCtxRef.current.drawImage(img, 0, 0)
          // Redraw preview
          if (previewMaskCtxRef.current && previewMaskCanvasRef.current) {
            previewMaskCtxRef.current.clearRect(0, 0, previewMaskCanvasRef.current.width, previewMaskCanvasRef.current.height)
            previewMaskCtxRef.current.drawImage(storedMaskCanvasRef.current, 0, 0)
            // Apply coral tint
            previewMaskCtxRef.current.globalCompositeOperation = 'source-in'
            previewMaskCtxRef.current.fillStyle = 'rgba(255, 90, 95, 0.86)'
            previewMaskCtxRef.current.fillRect(0, 0, previewMaskCanvasRef.current.width, previewMaskCanvasRef.current.height)
            previewMaskCtxRef.current.globalCompositeOperation = 'source-over'
          }
        }
      }
      img.src = stored.maskData
    }
  }, [maskSessionKey, imageUrl])

  // Update preview image when currentPreviewImage changes
  useEffect(() => {
    setPreviewImageMagicEraser(currentPreviewImage)
    // TODO: remove reason once the bug is fixed https://backbonephoto.atlassian.net/browse/BKBN-3750
    clearMask(false, true)

    // ignore clearMask dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPreviewImage])

  // Save mask to global storage whenever it changes
  useEffect(() => {
    if (canvasReady && storedMaskCanvasRef.current) {
      saveMaskToGlobalStorage()
    }
  }, [canvasReady, saveMaskToGlobalStorage, maskSessionKey])

  // Restore mask from global storage when component mounts
  useEffect(() => {
    if (canvasReady) {
      restoreMaskFromGlobalStorage()
    }
  }, [canvasReady, restoreMaskFromGlobalStorage, maskSessionKey])

  const syncCanvasSize = () => {
    const image = imageRef.current
    const previewMaskCanvas = previewMaskCanvasRef.current

    // Add null checks to prevent errors during image loading
    if (!image || !previewMaskCanvas || !storedMaskCanvasRef.current) {
      return
    }

    // Get the image's actual rendered size and position
    // With objectFit: 'contain', we need to calculate the actual content size, not the element size
    const container = image.parentElement
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const containerWidth = containerRect.width
    const containerHeight = containerRect.height

    // Get natural image dimensions
    const naturalWidth = image.naturalWidth
    const naturalHeight = image.naturalHeight

    if (naturalWidth === 0 || naturalHeight === 0) return

    // Calculate the actual rendered content size with objectFit: 'contain'
    // The image scales to fit while maintaining aspect ratio
    const naturalAspectRatio = naturalWidth / naturalHeight
    const containerAspectRatio = containerWidth / containerHeight

    let imageRenderedWidth: number
    let imageRenderedHeight: number

    if (naturalAspectRatio > containerAspectRatio) {
      // Image is wider - width is the limiting factor
      imageRenderedWidth = containerWidth
      imageRenderedHeight = containerWidth / naturalAspectRatio
    } else {
      // Image is taller - height is the limiting factor
      imageRenderedHeight = containerHeight
      imageRenderedWidth = containerHeight * naturalAspectRatio
    }

    // Calculate image position (centered with objectFit: 'contain')
    const imageLeft = (containerWidth - imageRenderedWidth) / 2
    const imageTop = (containerHeight - imageRenderedHeight) / 2

    const storedMaskCanvas = storedMaskCanvasRef.current!

    // Check if canvas dimensions actually need to change
    // Setting canvas.width or canvas.height clears the canvas content, so avoid if unchanged
    const dimensionsChanged = previewMaskCanvas.width !== naturalWidth ||
      previewMaskCanvas.height !== naturalHeight ||
      storedMaskCanvas.width !== naturalWidth ||
      storedMaskCanvas.height !== naturalHeight

    // Only update canvas buffer dimensions if they changed (this clears content)
    if (dimensionsChanged) {
      // Set canvas size to image's natural size for sharpness
      previewMaskCanvas.width = naturalWidth
      previewMaskCanvas.height = naturalHeight
      storedMaskCanvas.width = naturalWidth
      storedMaskCanvas.height = naturalHeight
    }

    // Always update CSS display size and position (these don't clear content)
    // Scale canvas display size to match image's actual rendered size EXACTLY
    // Ensure width matches preview image width precisely - use setProperty with important flag
    previewMaskCanvas.style.setProperty('width', `${imageRenderedWidth}px`, 'important')
    previewMaskCanvas.style.setProperty('height', `${imageRenderedHeight}px`, 'important')
    previewMaskCanvas.style.setProperty('max-width', `${imageRenderedWidth}px`, 'important')
    previewMaskCanvas.style.setProperty('max-height', `${imageRenderedHeight}px`, 'important')
    previewMaskCanvas.style.setProperty('min-width', `${imageRenderedWidth}px`, 'important')
    previewMaskCanvas.style.setProperty('min-height', `${imageRenderedHeight}px`, 'important')
    // Ensure no other width constraints interfere
    previewMaskCanvas.style.boxSizing = 'border-box'

    // Position canvas to match image position EXACTLY
    previewMaskCanvas.style.setProperty('left', `${imageLeft}px`, 'important')
    previewMaskCanvas.style.setProperty('top', `${imageTop}px`, 'important')
    previewMaskCanvas.style.setProperty('right', 'auto', 'important')
    previewMaskCanvas.style.setProperty('bottom', 'auto', 'important')

    // Position and size stored canvas to match preview canvas exactly
    // Width must match preview image width precisely - use setProperty with important flag
    storedMaskCanvas.style.setProperty('width', `${imageRenderedWidth}px`, 'important')
    storedMaskCanvas.style.setProperty('height', `${imageRenderedHeight}px`, 'important')
    storedMaskCanvas.style.setProperty('max-width', `${imageRenderedWidth}px`, 'important')
    storedMaskCanvas.style.setProperty('max-height', `${imageRenderedHeight}px`, 'important')
    storedMaskCanvas.style.setProperty('min-width', `${imageRenderedWidth}px`, 'important')
    storedMaskCanvas.style.setProperty('min-height', `${imageRenderedHeight}px`, 'important')
    // Ensure no other width constraints interfere
    storedMaskCanvas.style.boxSizing = 'border-box'
    storedMaskCanvas.style.setProperty('left', `${imageLeft}px`, 'important')
    storedMaskCanvas.style.setProperty('top', `${imageTop}px`, 'important')
    storedMaskCanvas.style.setProperty('right', 'auto', 'important')
    storedMaskCanvas.style.setProperty('bottom', 'auto', 'important')

    // Only clear and restore if dimensions changed (content was already cleared by setting width/height)
    if (dimensionsChanged) {
      const storedMaskCtx = storedMaskCanvas.getContext('2d', { willReadFrequently: true })!
      storedMaskCtxRef.current = storedMaskCtx

      const previewMaskCtx = previewMaskCanvas.getContext('2d', { willReadFrequently: true })!
      previewMaskCtxRef.current = previewMaskCtx

      setCanvasReady(true)
      restoreMaskFromGlobalStorage()
    } else if (!canvasReady) {
      // First time setup - get contexts and mark ready
      const storedMaskCtx = storedMaskCanvas.getContext('2d', { willReadFrequently: true })!
      storedMaskCtxRef.current = storedMaskCtx

      const previewMaskCtx = previewMaskCanvas.getContext('2d', { willReadFrequently: true })!
      previewMaskCtxRef.current = previewMaskCtx

      setCanvasReady(true)
      restoreMaskFromGlobalStorage()
    }
  }

  const getCanvasCoords = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = previewMaskCanvasRef.current
    const image = imageRef.current
    if (!canvas || !image) {
      return { x: 0, y: 0 }
    }

    const rect = canvas.getBoundingClientRect()
    // Adjust coordinates to match the actual canvas resolution
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }, [])

  const saveSnapshot = useCallback(() => {
    // Notify parent that mask changed
    if (onMaskChange) {
      onMaskChange()
    }
  }, [onMaskChange])

  const restoreSnapshot = (imageData: ImageData) => {
    const ctx = storedMaskCtxRef.current
    if (!ctx) {
      logger.warn('Stored mask context not ready for restoreSnapshot', undefined, {
        context: {
          component: 'MaskDrawer',
          action: 'restoreSnapshot'
        }
      })
      return
    }
    ctx.putImageData(imageData, 0, 0)
    redrawPreview()
  }

  /** Draws the selected object into its layer (natural size, like the stroke canvas) */
  const renderObjectsLayer = useCallback(() => {
    const storedMaskCanvas = storedMaskCanvasRef.current
    if (!storedMaskCanvas) return
    const layer = objectsCanvasRef.current ?? document.createElement('canvas')
    objectsCanvasRef.current = layer
    if (layer.width !== storedMaskCanvas.width || layer.height !== storedMaskCanvas.height) {
      layer.width = storedMaskCanvas.width
      layer.height = storedMaskCanvas.height
    }
    const layerCtx = layer.getContext('2d')
    if (!layerCtx) return
    layerCtx.clearRect(0, 0, layer.width, layer.height)
    const map = objectMapRef.current
    const object = map?.objects.get(selectedObjectIdRef.current)
    if (map && object) {
      const rect = labObjectRect(object, map, layer.width, layer.height)
      layerCtx.drawImage(object.mask, rect.x, rect.y, rect.width, rect.height)
    }
  }, [])

  const redrawPreview = useCallback(() => {
    const previewCtx = previewMaskCtxRef.current
    const previewCanvas = previewMaskCanvasRef.current
    const storedMaskCanvas = storedMaskCanvasRef.current

    // Add null checks to prevent errors
    if (!previewCtx || !previewCanvas || !storedMaskCanvas) {
      logger.warn('Canvas references not ready for redrawPreview', undefined, {
        context: {
          component: 'MaskDrawer',
          action: 'redrawPreview'
        }
      })
      return
    }

    previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height)

    // Step 1: Draw the white strokes from mask, and the selected object
    previewCtx.globalAlpha = 1
    previewCtx.drawImage(storedMaskCanvas, 0, 0)
    if (selectedObjectIdRef.current && objectsCanvasRef.current) {
      previewCtx.drawImage(objectsCanvasRef.current, 0, 0)
    }

    // Step 2: Apply coral tint using source-in blend
    previewCtx.globalCompositeOperation = 'source-in'
    previewCtx.fillStyle = 'rgba(255, 90, 95, 0.86)' // CORAL_600 with 86% opacity
    previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height)

    // Step 3: Reset blend mode
    previewCtx.globalCompositeOperation = 'source-over'
    previewCtx.globalAlpha = 1

    // Step 4: The object under the pointer — a light veil with a white glow (the Lab Playground's highlight)
    const map = objectMapRef.current
    const hovered = map?.objects.get(hoveredObjectIdRef.current)
    if (map && hovered) {
      const rect = labObjectRect(hovered, map, previewCanvas.width, previewCanvas.height)
      previewCtx.save()
      previewCtx.globalAlpha = 0.3
      previewCtx.shadowColor = 'rgba(255, 255, 255, 0.95)'
      previewCtx.shadowBlur = Math.max(8, previewCanvas.width / 120)
      previewCtx.drawImage(hovered.mask, rect.x, rect.y, rect.width, rect.height)
      previewCtx.restore()
    }
  }, [])

  // Commit stroke and update mask state
  const commitStrokeToHistory = useCallback(() => {
    const stroke = currentStrokeRef.current

    if (!stroke.path.length || !storedMaskCanvasRef.current || !storedMaskCtxRef.current) {
      return
    }

    // Track mask applied - increment counter and log analytics
    const newMaskCount = numberOfMasksApplied + 1
    setNumberOfMasksApplied(newMaskCount)

    if (logPhotoStudioEvent) {
      // Determine edition type based on context
      const editionType = isVisualStaging ? 'virtual_staging' : 'magic_eraser'

      // Try to get image metadata from the image URL (async, fire and forget)
      const getImageMetadataAsync = async () => {
        try {
          const response = await fetch(imageUrl)
          const blob = await response.blob()
          const file = new File([blob], sourceVisualFilename || 'image.jpg', { type: blob.type })
          const { getImageMetadata } = await import('utils/serialization/getImageMetadata')
          const metadata = await getImageMetadata(file)
          return {
            image_dimensions: `${metadata.width}x${metadata.height}`,
            image_format: file.type,
          }
        } catch (error) {
          // If metadata extraction fails, return empty object
          return {}
        }
      }

      getImageMetadataAsync().then(imageMetadata => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_MASK_APPLIED, {
          editionType,
          numberOfMasks: newMaskCount,
          maskType: stroke.maskType,
          assignmentId,
          sourceVisualFilename,
          ...imageMetadata,
        })
      }).catch(() => {
        // Fallback if metadata extraction fails
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_MASK_APPLIED, {
          editionType,
          numberOfMasks: newMaskCount,
          maskType: stroke.maskType,
          assignmentId,
          sourceVisualFilename,
        })
      })
    }

    // Apply the stroke to the mask (stroke is already drawn, just update preview)
    const applyStroke = async () => {
      // Redraw preview (stroke is already on the stored mask canvas)
      redrawPreview()

      // Update mask URL state to reflect the drawn stroke
      if (storedMaskCanvasRef.current) {
        const newMaskUrl = storedMaskCanvasRef.current.toDataURL('image/png')
        setMaskUrl(newMaskUrl)
      }

      // Notify parent
      if (onMaskChange) {
        onMaskChange()
      }
    }

    applyStroke()

    // Reset current stroke
    currentStrokeRef.current = {
      path: [],
      brushSize: 0,
      maskType: stroke.maskType,
      isDrawing: false
    }
  }, [onMaskChange, redrawPreview, numberOfMasksApplied, logPhotoStudioEvent, assignmentId, sourceVisualFilename, imageUrl, isVisualStaging])

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.PointerEvent<HTMLCanvasElement>) => {
    const { x, y } = getCanvasCoords(e)

    const storedMaskCtx = storedMaskCtxRef.current!

    // Calculate scale factor to maintain consistent brush size across different image resolutions
    const image = imageRef.current
    const canvas = previewMaskCanvasRef.current
    if (!image || !canvas) return

    // Get the image's actual rendered size using getBoundingClientRect
    const imageRect = image.getBoundingClientRect()
    const imageRenderedWidth = imageRect.width
    const imageRenderedHeight = imageRect.height

    // Calculate scale factors based on actual rendered size
    const scaleX = canvas.width / imageRenderedWidth
    const scaleY = canvas.height / imageRenderedHeight
    const scaleFactor = Math.max(scaleX, scaleY) // Use the larger scale factor to ensure consistency
    const scaledBrushSize = brushSize * scaleFactor

    // --- Draw solid white into stored mask ---
    storedMaskCtx.strokeStyle = WHITE
    storedMaskCtx.fillStyle = WHITE
    storedMaskCtx.lineWidth = scaledBrushSize
    storedMaskCtx.lineCap = 'round'
    storedMaskCtx.lineJoin = 'round'

    if (lastPosRef.current) {
      storedMaskCtx.beginPath()
      storedMaskCtx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
      storedMaskCtx.lineTo(x, y)
      storedMaskCtx.stroke()
    } else {
      // Don't draw a dot on initial mouse down - just set the position for the next stroke
      // This prevents unwanted dots when redoing masks
      storedMaskCtx.beginPath()
      storedMaskCtx.moveTo(x, y)
      // Don't call stroke() here to avoid creating a dot
    }

    // --- Render coral preview from white mask (and the selected object) ---
    redrawPreview()

    lastPosRef.current = { x, y }

    // Save mask to global storage after drawing
    saveMaskToGlobalStorage()
  }, [brushSize, getCanvasCoords, imageRef, previewMaskCanvasRef, redrawPreview, saveMaskToGlobalStorage])

  // Reset lastPosRef on mouse up/leave
  useEffect(() => {
    if (!isDrawing) lastPosRef.current = null
  }, [isDrawing])

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // A press on a detected object is handled by the pointer handlers (click or stroke)
    if (pendingTapRef.current) return
    // Initialize stroke data with current brush size
    const { x, y } = getCanvasCoords(e)
    const maskType = isVisualStaging ? MaskDrawingType.VIRTUAL_STAGING : MaskDrawingType.MAGIC_ERASER
    currentStrokeRef.current = {
      path: [{ x, y }],
      brushSize,
      maskType,
      isDrawing: true
    }

    saveSnapshot()
    setIsDrawing(true)

    draw(e)
  }, [saveSnapshot, draw, brushSize, isVisualStaging, getCanvasCoords])

  const handleMouseUp = useCallback(() => {
    setIsDrawing(false)
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (pendingTapRef.current) return
    if (isDrawing) {
      // Add point to stroke path for unified history system
      if (currentStrokeRef.current.isDrawing) {
        const { x, y } = getCanvasCoords(e)
        currentStrokeRef.current.path.push({ x, y })
      }

      draw(e)
    }
    // ignore draw, getCanvasCoords dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing])

  /** The detected object under the pointer (0 for none, or when the Lab objects are not available) */
  const objectIdAt = useCallback((e: React.PointerEvent<HTMLCanvasElement>): number => {
    const map = objectMapRef.current
    const canvas = previewMaskCanvasRef.current
    if (!map || !canvas) return 0
    const { x, y } = getCanvasCoords(e)
    return labObjectIdAt(map, x, y, canvas.width, canvas.height)
  }, [getCanvasCoords])

  const setHovered = useCallback((hovered: { id: number, x: number, y: number } | null) => {
    const id = hovered?.id ?? 0
    const hasChanged = id !== hoveredObjectIdRef.current
    hoveredObjectIdRef.current = id
    setHoveredObject(hovered)
    if (hasChanged) redrawPreview()
  }, [redrawPreview])

  /** Hover highlight + hint position (relative to the drawer frame) while the pointer moves without drawing */
  const updateHoveredObject = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const id = objectIdAt(e)
    if (!id) {
      if (hoveredObjectIdRef.current) setHovered(null)
      return
    }
    const frame = e.currentTarget.parentElement?.getBoundingClientRect()
    setHovered({ id, x: e.clientX - (frame?.left ?? 0), y: e.clientY - (frame?.top ?? 0) })
  }, [objectIdAt, setHovered])

  /** Click on an object: select it (replacing any other), or let go of it when it was the selected one */
  const selectObject = useCallback((objectId: number) => {
    const next = selectedObjectIdRef.current === objectId ? 0 : objectId
    selectedObjectIdRef.current = next
    setSelectedObjectId(next)
    renderObjectsLayer()
    redrawPreview()
    onMaskChange?.()
    logPhotoStudioEvent?.(AnalyticsEvent.PHOTO_STUDIO_MASK_APPLIED, {
      editionType: 'magic_eraser',
      maskType: MaskDrawingType.MAGIC_ERASER,
      selection: 'object',
      isSelected: next !== 0,
      concept: objectMapRef.current?.objects.get(objectId)?.concept,
      assignmentId,
      sourceVisualFilename,
    })
  }, [assignmentId, logPhotoStudioEvent, onMaskChange, redrawPreview, renderObjectsLayer, sourceVisualFilename])

  // Add pointer event handlers for better touch support
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
    const objectId = objectIdAt(e)
    if (objectId) {
      // Maybe a click on the object: nothing is painted unless the press travels like a stroke (handlePointerMove)
      const { x, y } = getCanvasCoords(e)
      pendingTapRef.current = { x, y, clientX: e.clientX, clientY: e.clientY, objectId }
      return
    }
    saveSnapshot()
    setIsDrawing(true)

    // Initialize stroke data for unified history
    const { x, y } = getCanvasCoords(e)
    const maskType = isVisualStaging ? MaskDrawingType.VIRTUAL_STAGING : MaskDrawingType.MAGIC_ERASER
    currentStrokeRef.current = {
      path: [{ x, y }],
      brushSize,
      maskType,
      isDrawing: true
    }

    draw(e)
  }, [saveSnapshot, draw, isVisualStaging, getCanvasCoords, brushSize, objectIdAt])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    try { (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId) } catch { }

    const pendingTap = pendingTapRef.current
    if (pendingTap) {
      pendingTapRef.current = null
      selectObject(pendingTap.objectId)
      return
    }

    if (isDrawing) {
      if (currentStrokeRef.current.isDrawing && currentStrokeRef.current.path.length > 0) {
        commitStrokeToHistory()
      }
    }

    setIsDrawing(false)

  }, [isDrawing, commitStrokeToHistory, selectObject])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const pendingTap = pendingTapRef.current
    if (pendingTap) {
      if (Math.hypot(e.clientX - pendingTap.clientX, e.clientY - pendingTap.clientY) < OBJECT_TAP_TOLERANCE_PX) return
      // A stroke after all, painted from where the press started
      pendingTapRef.current = null
      setHovered(null)
      saveSnapshot()
      setIsDrawing(true)
      currentStrokeRef.current = {
        path: [{ x: pendingTap.x, y: pendingTap.y }],
        brushSize,
        maskType: isVisualStaging ? MaskDrawingType.VIRTUAL_STAGING : MaskDrawingType.MAGIC_ERASER,
        isDrawing: true
      }
      lastPosRef.current = { x: pendingTap.x, y: pendingTap.y }
      draw(e)
      return
    }
    if (!isDrawing) {
      updateHoveredObject(e)
      return
    }
    // Add point to stroke path for unified history system
    if (currentStrokeRef.current.isDrawing) {
      const { x, y } = getCanvasCoords(e)
      currentStrokeRef.current.path.push({ x, y })
    }

    draw(e)
  }, [isDrawing, draw, getCanvasCoords, brushSize, isVisualStaging, saveSnapshot, setHovered, updateHoveredObject])

  const handlePointerLeave = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    // Leaving mid-press is not a click
    pendingTapRef.current = null
    if (hoveredObjectIdRef.current) setHovered(null)
    handlePointerUp(e)
  }, [handlePointerUp, setHovered])

  /** Brush circle, or a pointer over a selectable object (useDynamicCursor) */
  const getCursorOverride = useCallback(() => (hoveredObjectIdRef.current ? 'pointer' : undefined), [])

  /**
   * The mask as a PNG at the image's natural size. The ai-visuals (Propershot) eraser reads white strokes on an opaque
   * black background; the Lab eraser reads the alpha channel, so `transparent` exports a binary white-on-transparent
   * mask instead (see `lab/labMask.ts`) — and null when nothing is marked.
   */
  const exportMaskAsFile = (options: { transparent?: boolean } = {}): Promise<File | null> => {
    return new Promise((resolve) => {
      const maskCanvas = storedMaskCanvasRef.current!
      const { width, height } = previewImageSizeRef.current
      if (!maskCanvas || !width || !height) {
        resolve(null)
        return
      }

      const exportCanvas = document.createElement('canvas')
      exportCanvas.width = width
      exportCanvas.height = height

      const exportCtx = exportCanvas.getContext('2d', { willReadFrequently: true })!
      const objectsLayer = selectedObjectIdRef.current ? objectsCanvasRef.current : null
      if (options.transparent) {
        exportCtx.drawImage(maskCanvas, 0, 0, width, height)
        if (objectsLayer) exportCtx.drawImage(objectsLayer, 0, 0, width, height)
        const imageData = exportCtx.getImageData(0, 0, width, height)
        if (!binarizeLabMaskPixels(imageData.data)) {
          resolve(null)
          return
        }
        exportCtx.putImageData(imageData, 0, 0)
      } else {
        // Fill black background for mask image export
        exportCtx.fillStyle = BLACK_TEXT
        exportCtx.fillRect(0, 0, width, height)

        // Scale and draw white strokes from stored canvas
        exportCtx.drawImage(maskCanvas, 0, 0, width, height)
        if (objectsLayer) exportCtx.drawImage(objectsLayer, 0, 0, width, height)
      }

      exportCanvas.toBlob((blob) => {
        if (blob) {
          const randomHash = Math.random().toString(36).substring(2, 7)
          const file = new File([blob], `mask_${assignmentId}_${randomHash}${FileType.PNG}`, { type: PhotoStudioFileType.MASK })
          resolve(file)
        } else {
          resolve(null)
        }
      }, PhotoStudioFileType.MASK)
    })
  }

  // Visual Staging: apply ImageData to stored mask canvas and redraw preview
  const applyMaskImageData = useCallback((imageData: ImageData | null) => {
    if (!storedMaskCtxRef.current || !storedMaskCanvasRef.current) return
    const ctx = storedMaskCtxRef.current
    const canvas = storedMaskCanvasRef.current
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (imageData) {
      try {
        ctx.putImageData(imageData, 0, 0)
      } catch { }
    }
    // trigger preview overlay refresh by drawing a no-op
    previewMaskCtxRef.current && previewMaskCtxRef.current.clearRect(0, 0, previewMaskCanvasRef.current!.width, previewMaskCanvasRef.current!.height)
  }, [])

  const revertToOriginal = useCallback(() => {
    // Clear mask
    if (storedMaskCtxRef.current) {
      storedMaskCtxRef.current.clearRect(0, 0, storedMaskCanvasRef.current!.width, storedMaskCanvasRef.current!.height)
    }
    if (previewMaskCtxRef.current) {
      previewMaskCtxRef.current.clearRect(0, 0, previewMaskCanvasRef.current!.width, previewMaskCanvasRef.current!.height)
    }
    setMaskUrl(null)
    lastPosRef.current = null

    // Reset preview image to original
    setPreviewImageMagicEraser(imageUrl)
    if (onPreviewImageChange) {
      onPreviewImageChange(imageUrl)
    }
  }, [imageUrl, onPreviewImageChange])

  const hasMaskDrawn = useCallback((): boolean => {
    // A selected object is a mask too
    if (selectedObjectIdRef.current) return true
    // Check if there's a mask drawn by checking if the stored mask canvas has content
    if (!storedMaskCanvasRef.current) return false

    // Check if canvas has valid dimensions (not initialized yet)
    if (storedMaskCanvasRef.current.width === 0 || storedMaskCanvasRef.current.height === 0) {
      return false
    }

    const ctx = storedMaskCanvasRef.current.getContext('2d', { willReadFrequently: true })
    if (!ctx) return false

    const imageData = ctx.getImageData(0, 0, storedMaskCanvasRef.current.width, storedMaskCanvasRef.current.height)
    return imageData.data.some(pixel => pixel !== 0) // Check if any pixel is not transparent
  }, [])

  // Helper function to actually perform the clear mask operation
  const performClearMask = useCallback(() => {
    // Clear both stored and preview mask canvases
    if (storedMaskCtxRef.current) {
      storedMaskCtxRef.current.clearRect(0, 0, storedMaskCanvasRef.current!.width, storedMaskCanvasRef.current!.height)
    }

    if (previewMaskCtxRef.current) {
      previewMaskCtxRef.current.clearRect(0, 0, previewMaskCanvasRef.current!.width, previewMaskCanvasRef.current!.height)
    }

    // Clear mask URL
    setMaskUrl(null)

    // Let go of the selected object
    selectedObjectIdRef.current = 0
    setSelectedObjectId(0)
    renderObjectsLayer()

    // Reset mask counter when mask is cleared
    setNumberOfMasksApplied(0)

    // Clear mask from global storage to prevent restoration
    globalMaskStorage.delete(maskSessionKey)

    // Reset last position to prevent cursor position from being remembered
    lastPosRef.current = null
  }, [maskSessionKey, renderObjectsLayer])

  const clearMask = useCallback((shouldLogToHistory: boolean = true, shouldNotifyParent: boolean = true) => {
    // Check if there's actually a mask to clear
    const hasExistingMask = hasMaskDrawn()

    // Log analytics for clear mask button click (only if there's a mask to clear and it's a user action)
    if (logPhotoStudioEvent && hasExistingMask && shouldLogToHistory) {
      const maskType = isVisualStaging ? MaskDrawingType.VIRTUAL_STAGING : MaskDrawingType.MAGIC_ERASER
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_CLEAR_MASK, {
        assignmentId,
        sourceVisualFilename,
        maskType,
      })
    }

    // Only log to history if there was actually a mask to clear and we want to log it
    performClearMask()

    if (onMaskChange && shouldNotifyParent) {
      onMaskChange()
    }
  }, [isVisualStaging, onMaskChange, hasMaskDrawn, performClearMask, logPhotoStudioEvent, assignmentId, sourceVisualFilename])

  // Clear mask when clearMaskTrigger changes (don't log to history for automatic clears)
  useEffect(() => {
    if (clearMaskTrigger !== undefined) {
      clearMask(false, false) // Don't log automatic clears to history and don't notify parent
    }
  }, [clearMaskTrigger, clearMask])

  // Wrapper function to track brush size changes
  const updateBrushSize = useCallback((newBrushSize: number) => {
    const oldBrushSize = brushSize
    setBrushSize(newBrushSize)

    onMaskChange?.()

    // Log analytics for brush size change
    if (logPhotoStudioEvent && oldBrushSize !== newBrushSize) {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_BRUSH_SIZE_CHANGED, {
        assignmentId,
        sourceVisualFilename,
        oldBrushSize,
        newBrushSize,
        maskType: isVisualStaging ? MaskDrawingType.VIRTUAL_STAGING : MaskDrawingType.MAGIC_ERASER,
      })

      // Track edition selected for Magic Eraser when brush size changes
      if (!isVisualStaging) {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
          editionType: getEditionTypeString(EditionType.MAGIC_ERASER),
          editionSettings: {
            brushSize: newBrushSize,
          },
          assignmentId,
          sourceVisualFilename,
        })
      }
    }
  }, [brushSize, onMaskChange, logPhotoStudioEvent, assignmentId, sourceVisualFilename, isVisualStaging])

  const resetBrushSize = useCallback(() => {
    updateBrushSize(DEFAULT_BRUSH_SIZE)
  }, [updateBrushSize])

  return {
    previewImageMagicEraser,
    setPreviewImageMagicEraser,
    saveSnapshot,
    restoreSnapshot,
    exportMaskAsFile,
    clearMask,
    revertToOriginal,
    hasMaskDrawn,
    brushSize,
    isApplyingChanges,
    imageRef,
    previewMaskCanvasRef,
    storedMaskCanvasRef,
    isDrawing,
    setIsDrawing,
    getCanvasCoords,
    syncCanvasSize,
    redrawPreview,
    maskUrl,
    setMaskUrl,
    canvasReady,
    setBrushSize,
    updateBrushSize,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handlePointerDown,
    handlePointerUp,
    handlePointerMove,
    previewImageSizeRef,
    resetBrushSize,
    numberOfMasksApplied,
    isProcessingImage: isProcessingImage ?? false,
    isSaving: isSaving ?? false,
    handlePointerLeave,
    getCursorOverride,
    selectedObjectId,
    hoveredObject,
    objectsStatus: labObjects?.status,
  }
})
